import * as AccordionPrimitive from "@radix-ui/react-accordion"

import { ArrowDown } from "../../images/xd/icons"
import { keyframes, styled, theme } from "../../styles/stitches.config"
import { defaultAnimationDuration } from "../../styles/utils"
import { ComponentProps, ComponentPropsWithRef } from "react"
import { Div } from "../div/Div"

const Accordion = AccordionPrimitive.Root
const AccordionItem = AccordionPrimitive.Item

const SHeader = styled(AccordionPrimitive.Header, {
  all: "unset",
  display: "flex",
})

const STrigger = styled(AccordionPrimitive.Trigger, {
  all: "unset",
  transition: "all",

  display: "flex",
  flex: 1,
  justifyContent: "space-between",
  alignItems: "center",

  cursor: "pointer",
  paddingTop: theme.space.s4,
  paddingBottom: theme.space.s4,

  "@md": {
    cursor: "auto",
  },
})

export const SArrow = styled(ArrowDown, {
  width: theme.sizes.s6,
  height: theme.sizes.s6,

  transition: `transform ${defaultAnimationDuration}ms ease-out`,
  "[data-state=open] &": { transform: "rotate(180deg)" },

  variants: {
    variant: {
      primary: {
        color: theme.colors.primary,
      },
      secondary: {
        color: theme.colors.secondary,
      },
    },
  },

  defaultVariants: {
    variant: "primary",
  },
})

type AccordionTriggerProps = ComponentPropsWithRef<typeof STrigger> & {
  chevronColor?: ComponentProps<typeof SArrow>["variant"]
  hideChevron?: boolean
}

function AccordionTrigger({ ref, children, chevronColor, hideChevron, ...props }: AccordionTriggerProps) {
  return (
    <SHeader>
      <STrigger ref={ref} {...props}>
        {children}
        {!hideChevron && <SArrow variant={chevronColor} />}
      </STrigger>
    </SHeader>
  )
}

const slideDown = keyframes({
  from: { height: 0 },
  to: { height: "var(--radix-accordion-content-height)" },
})

const slideUp = keyframes({
  from: { height: "var(--radix-accordion-content-height)" },
  to: { height: 0 },
})

const SContent = styled(AccordionPrimitive.Content, {
  overflow: "hidden",
  transition: "all",

  noReducedMotion: {
    '&[data-state="open"]': {
      animation: `${slideDown} ${defaultAnimationDuration}ms ease-out`,
    },
    '&[data-state="closed"]': {
      animation: `${slideUp} ${defaultAnimationDuration}ms ease-out`,
    },
  },
})

function AccordionContent({ children, css, ref, ...props }: ComponentPropsWithRef<typeof SContent>) {
  return (
    <SContent ref={ref} {...props}>
      <Div
        css={{
          paddingBottom: theme.space.s4,
          paddingTop: theme.space.s0,
          ...css,
        }}
      >
        {children}
      </Div>
    </SContent>
  )
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger }
